/**
 * Pure logic of the users screen (port of UsersComponent): role labels,
 * "Assigned Stores" summary, filtering by store selection and platform, sorting
 * by column.
 */
import { PLATFORM, ROLES, STORE_ALL, type Platform } from '@/shared/constants';
import { compareCodeUnits } from '@/shared/lib/compare';
import type { Store, StoreId } from '@/shared/config/app-config';
import type { Workshop } from '@/features/stores/api';
import type { UserRecord } from './api';

const ROLE_LABELS: Record<string, string> = {
  [ROLES.ADMIN]: 'Admin',
  [ROLES.HEAD_OFFICE]: 'Head Office',
  [ROLES.BOUTIQUE_MANAGER]: 'Boutique Manager',
  [ROLES.SALES_ASSOCIATE]: 'Sales Associate',
  [ROLES.CALL_CENTER]: 'Call Center',
  [ROLES.DHL]: 'DHL',
  [ROLES.WORKSHOP]: 'Workshop',
};

/** Label of a role, the raw value for an unknown one. */
export const roleName = (role: string | null | undefined): string =>
  ROLE_LABELS[role ?? ''] ?? role ?? '';

/**
 * "Assigned Stores" cell: the store name, the country when every store is in
 * the same country, the region when every store is in the same region,
 * otherwise "multiple selection".
 */
export const storeSummary = (
  stores: readonly Store[],
  assignedStores: readonly string[] | null | undefined,
): string => {
  const assigned = new Set(assignedStores ?? []);
  const userStores = stores.filter((store) => assigned.has(String(store.id)));
  if (userStores.length === 0) return '';
  if (userStores.length === 1) return userStores[0]!.name;
  const [first] = userStores;
  if (userStores.every((store) => store.country === first!.country))
    return first!.country ?? '';
  if (userStores.every((store) => store.region === first!.region))
    return first!.region ?? '';
  return 'multiple selection';
};

/**
 * Users to list: those matching the store selection of the top menu (all of
 * them when "ALL" is selected, those without stores when nothing is), then
 * those with access to the platform.
 */
export const visibleUsers = (
  users: readonly UserRecord[],
  platform: Platform,
  selectedIds: readonly StoreId[],
): UserRecord[] => {
  const allStores = selectedIds.some((id) => Number(id) === STORE_ALL.id);
  const selected = new Set(selectedIds.map(String));
  return users.filter((user) => {
    const assigned = user.assignedStores ?? [];
    if (!allStores) {
      const bothEmpty = selected.size === 0 && assigned.length === 0;
      const intersects = assigned.some((id) => selected.has(id));
      if (!bothEmpty && !intersects) return false;
    }
    return platform === PLATFORM.DMS ? !!user.dmsAccess : !!user.abcdiorAccess;
  });
};

/** Workshop names of a user, joined for the column. */
export const workshopNames = (
  user: UserRecord,
  workshops: readonly Workshop[],
): string => {
  const ids = new Set(user.workshopIds ?? []);
  return workshops
    .filter((workshop) => ids.has(workshop.id))
    .map((workshop) => workshop.name ?? '')
    .join(', ');
};

export type SortKey = 'username' | 'email' | 'role' | 'villaDior';

export interface SortState {
  by: SortKey;
  reverse: boolean;
}

export const DEFAULT_SORT: SortState = { by: 'username', reverse: false };

/** Click on a sortable header: same column toggles the direction. */
export const nextSort = (current: SortState, by: SortKey): SortState =>
  current.by === by
    ? { by, reverse: !current.reverse }
    : { by, reverse: false };

const lowerName = (user: UserRecord) =>
  String(user.username ?? '').toLowerCase();

/**
 * Stable sort on the lower-cased raw value, reversed on demand (lodash sortBy
 * + reverse). "Villa Dior" puts the flagged users first (last when reversed),
 * each group by user name (lodash orderBy).
 */
export const sortUsers = (
  users: readonly UserRecord[],
  sort: SortState,
): UserRecord[] => {
  const { by } = sort;
  if (by === 'villaDior') {
    const rank = (user: UserRecord) => (user.dmsSettings?.villaDior ? 0 : 1);
    return [...users].sort(
      (a, b) =>
        (sort.reverse ? rank(b) - rank(a) : rank(a) - rank(b)) ||
        compareCodeUnits(lowerName(a), lowerName(b)),
    );
  }
  const key = (user: UserRecord) => String(user[by] ?? '').toLowerCase();
  const sorted = [...users].sort((a, b) => compareCodeUnits(key(a), key(b)));
  return sort.reverse ? sorted.reverse() : sorted;
};
